import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Eraser, Loader2, PenLine, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_CONSENT =
  "I confirm that the signer reviewed and signed this document on the clinic device.";

function relationshipRequired(role) {
  return role === "guardian" || role === "representative";
}

export default function SignatureDialog({
  open,
  onOpenChange,
  context,
  defaultSignerName = "",
  busy = false,
  onSubmit,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [signerRole, setSignerRole] = useState("patient");
  const [signerName, setSignerName] = useState(defaultSignerName || "");
  const [relationship, setRelationship] = useState("");
  const [witnessName, setWitnessName] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSignerRole("patient");
    setSignerName(defaultSignerName || "");
    setRelationship("");
    setWitnessName("");
    setConfirmed(false);
    setHasInk(false);
    window.requestAnimationFrame(() => resetCanvas(canvasRef.current));
  }, [defaultSignerName, open]);

  const canSubmit = useMemo(() => {
    if (busy || !hasInk || !confirmed) return false;
    if (!signerName.trim() || !witnessName.trim()) return false;
    if (relationshipRequired(signerRole) && !relationship.trim()) return false;
    return true;
  }, [busy, confirmed, hasInk, relationship, signerName, signerRole, witnessName]);

  const start = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas || busy) return;
    drawingRef.current = true;
    const ctx = canvas.getContext("2d");
    const point = canvasPoint(canvas, event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [busy]);

  const draw = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas || !drawingRef.current || busy) return;
    const ctx = canvas.getContext("2d");
    const point = canvasPoint(canvas, event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    setHasInk(true);
  }, [busy]);

  const end = useCallback((event) => {
    drawingRef.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const clear = useCallback(() => {
    if (busy) return;
    resetCanvas(canvasRef.current);
    setHasInk(false);
  }, [busy]);

  const submit = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !canSubmit) return;
    const signature = {
      signer_name: signerName.trim(),
      signer_role: signerRole,
      signer_relationship: relationshipRequired(signerRole) ? relationship.trim() : null,
      staff_witness_name: witnessName.trim(),
      signature_image_data_url: canvas.toDataURL("image/png"),
      consent_text: DEFAULT_CONSENT,
    };
    await onSubmit?.(signature);
  }, [canSubmit, onSubmit, relationship, signerName, signerRole, witnessName]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange?.(next); }}>
      <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-base text-slate-900">
            <PenLine className="w-4 h-4 text-teal-700" />
            Sign document
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {context?.title || "Patient document"}
            {context?.documentNumber ? ` · ${context.documentNumber}` : ""}
            {context?.patientName ? ` · ${context.patientName}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Signer</span>
              <select
                value={signerRole}
                onChange={(event) => setSignerRole(event.target.value)}
                disabled={busy}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50"
              >
                <option value="patient">Patient</option>
                <option value="guardian">Guardian</option>
                <option value="representative">Representative</option>
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Signer name</span>
              <input
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                disabled={busy}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {relationshipRequired(signerRole) ? "Relationship" : "Relationship optional"}
              </span>
              <input
                value={relationship}
                onChange={(event) => setRelationship(event.target.value)}
                disabled={busy || !relationshipRequired(signerRole)}
                placeholder={relationshipRequired(signerRole) ? "Parent, guardian, spouse" : ""}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50"
              />
            </label>
          </div>

          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Staff witness</span>
            <input
              value={witnessName}
              onChange={(event) => setWitnessName(event.target.value)}
              disabled={busy}
              placeholder="Name of staff member presenting this document"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50"
            />
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Signature pad</span>
              <button
                type="button"
                onClick={clear}
                disabled={busy || !hasInk}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <Eraser className="w-3 h-3" />
                Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={720}
              height={220}
              onPointerDown={start}
              onPointerMove={draw}
              onPointerUp={end}
              onPointerLeave={end}
              className="block h-52 w-full touch-none rounded-lg border border-slate-300 bg-slate-50"
              aria-label="Signature drawing pad"
            />
          </div>

          <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              disabled={busy}
              className="mt-0.5"
            />
            <span>{DEFAULT_CONSENT}</span>
          </label>
        </div>

        <DialogFooter className="px-5 py-4 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Confirm signature
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function resetCanvas(canvas) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#0f172a";
}

function canvasPoint(canvas, event) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / Math.max(rect.width, 1);
  const scaleY = canvas.height / Math.max(rect.height, 1);
  return {
    x: (event.clientX - rect.left) * scaleX,
    y: (event.clientY - rect.top) * scaleY,
  };
}
